//* 담당 지도사 변경 (관리자 전용) — 부고 관리 카드 > 담당 지도사 변경
//* 상가(fevent)·부고(bugo)·상주(member)의 user를 새 지도사로 한 번에 재귀속하고,
//* 새 지도사의 상조가 다르면 fevent·bugo의 bugoBrand도 함께 바꾼다.
//* 스펙: docs/fevent-change-worker-spec.md
import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { useRecoilValue, useSetRecoilState } from 'recoil';

import { BugoBrandBrief } from '@shared/api/bugoBrand/bugoBrand.interface';
import { feventStateSelectorFamily } from '@shared/api/fevent/fevent.selector';
import {
  ChangeWorkerUnavailableError,
  changeFeventWorker,
} from '@shared/api/fevent/feventWorkerChange.controller';
import { adminBugoWorkerSearch } from '@shared/api/user/user.controller';
import { Button } from '@shared/components/Button';
import DismissModal from '@shared/components/DismissModal';
import { Loading } from '@shared/components/Loading';
import { invalidateQueriesByUrlPart } from '@shared/hooks/recoil-query';
import {
  notificateError,
  notificateSuccess,
} from '@shared/plugIn/ant-notification/ant-notifiaction';
import { api_ } from '@shared/plugIn/axios';
import { simpleOpenAtomFamily } from '@shared/state/atom';
import { notificationInstanceAtom } from '@shared/state/atom/notification.atom';
import { useAuth } from '@shared/state/hooks/useAuth';
import { UserRole } from '@shared/types';
import { phoneNumberStringFormator } from '@shared/utils/phoneNumberStringFormator';
import { useQueryClient } from 'react-query';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useAsync, useAsyncFn } from 'react-use';

import { useTitleHook } from '../hooks/useTitleHook';
import {
  WorkerCandidate,
  brandIdOf,
  buildWorkerChangePlan,
  candidateBrand,
  candidateBrandName,
  toChangeWorkerDto,
} from '../utils/feventWorkerChange.util';

const ROLE_LABEL: Partial<Record<UserRole, string>> = {
  [UserRole.BugoAgencyWorker]: '지도사',
  [UserRole.BugoAgencyBranchAdmin]: '지점관리자',
  [UserRole.BugoAgencyAdmin]: '상조관리자',
};

const roleBadges = (roles: UserRole[] | undefined) =>
  (roles ?? [])
    .filter((role) => ROLE_LABEL[role])
    .map((role) => (
      <span
        key={role}
        className="rounded bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-gray-600"
      >
        {ROLE_LABEL[role]}
      </span>
    ));

const BRAND_BRIEF_SELECT = 'type bugoAgency.nickName bugoAgency.corpName bugoAgencyList';

//* 상가 상세(feventHookUrl)는 bugoBrand를 populate하지 않는다 → 이름 필드만 별도 조회
const useBrandBrief = (brandId?: string) => {
  const { value } = useAsync(async () => {
    if (!brandId) return undefined;
    try {
      const { data } = await api_.get<BugoBrandBrief>(
        `admin/bugo-brand/${brandId}?select=${encodeURIComponent(BRAND_BRIEF_SELECT)}`,
      );
      return data;
    } catch (err) {
      console.error(err);
      return undefined;
    }
  }, [brandId]);
  return value;
};

const CONFIRM_OPEN_ID = 'fevent-worker-change-confirm';

export const FeventWorkerChangePage: FC = function FeventWorkerChangePage() {
  useTitleHook('담당 지도사 변경');
  const { isAdmin } = useAuth();
  const { feventId } = useParams();
  const feventState = useRecoilValue(feventStateSelectorFamily(feventId ?? 'undefined'));
  const fevent = feventState.status === 'success' ? feventState.data : null;

  const queryClient = useQueryClient();
  const notifiacationInstance = useRecoilValue(notificationInstanceAtom);
  const navigate = useNavigate();
  const setConfirmOpen = useSetRecoilState(simpleOpenAtomFamily(CONFIRM_OPEN_ID));

  const [keyword, setKeyword] = useState('');
  //* 기본은 같은 상조만 — 다른 상조로 넘기는 건 드문 작업이라 명시적으로 끄게 한다(실수 방지)
  const [sameBrandOnly, setSameBrandOnly] = useState(true);
  const [selected, setSelected] = useState<WorkerCandidate | null>(null);
  const [chosenAgencyIndex, setChosenAgencyIndex] = useState<number | undefined>(
    undefined,
  );

  const currentBrandId = brandIdOf(fevent?.bugoBrand as unknown as string | undefined);
  const currentBrand = useBrandBrief(currentBrandId);
  const currentBrandName = candidateBrandName(currentBrand);

  const [{ loading: searching, value: results }, search] = useAsyncFn(
    async (kw: string, brandId?: string) => {
      try {
        const list = await adminBugoWorkerSearch<WorkerCandidate>(kw, brandId);
        //* 서버 필터는 user.bugoBrand 기준 — 상조관리자(virtual 브랜드)까지 소속 상조로 한 번 더 거른다
        return brandId
          ? list.filter((user) => brandIdOf(candidateBrand(user)) === brandId)
          : list;
      } catch (err) {
        console.error(err);
        notificateError(notifiacationInstance, '검색에 실패했습니다.');
        return [] as WorkerCandidate[];
      }
    },
    [notifiacationInstance],
  );

  const brandFilterId = sameBrandOnly ? currentBrandId : undefined;
  const feventLoaded = Boolean(fevent);

  //* 진입·상조 필터 전환 시 재조회 (상가 로드 전엔 상조 id가 없어 기다린다)
  useEffect(() => {
    if (!feventLoaded) return;
    search(keyword, brandFilterId);
    //* keyword는 검색 버튼/Enter로만 반영
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feventLoaded, brandFilterId]);

  //* 필터를 바꾸면 이전 선택은 무효 (다른 상조 후보가 남아 있으면 실수의 원인)
  useEffect(() => {
    setSelected(null);
    setChosenAgencyIndex(undefined);
  }, [sameBrandOnly]);

  const onSelect = useCallback((user: WorkerCandidate) => {
    setSelected(user);
    //* 후보가 바뀌면 이전에 고른 행사 상조는 무효
    setChosenAgencyIndex(undefined);
  }, []);

  const plan = useMemo(
    () =>
      fevent && selected
        ? buildWorkerChangePlan(fevent, selected, chosenAgencyIndex)
        : null,
    [fevent, selected, chosenAgencyIndex],
  );

  const targetBrand = selected ? candidateBrand(selected) : undefined;
  const targetAgencyList =
    targetBrand && typeof targetBrand !== 'string'
      ? targetBrand.bugoAgencyList ?? []
      : [];

  const [{ loading: executing }, execute] = useAsyncFn(async () => {
    if (!fevent || !plan || plan.noop || plan.needsAgencyIndex) return;
    try {
      const result = await changeFeventWorker(fevent._id, toChangeWorkerDto(plan));
      //* 상가 상세·부고 목록·통계 등 fevent 축 조회 전부 재조회 (문자열 키는 prefix 매칭 불가)
      await invalidateQueriesByUrlPart(queryClient, '/fevent');
      const counts =
        result.updated && (result.updated.bugos ?? result.updated.members) !== undefined
          ? ` (상주 ${result.updated.members ?? '-'}명 · 부고 ${
              result.updated.bugos ?? '-'
            }건)`
          : '';
      notificateSuccess(
        notifiacationInstance,
        '담당 지도사를 변경했습니다.',
        `${selected?.info?.name ?? ''}${counts}`,
      );
      navigate('/fevent/manage');
    } catch (err) {
      console.error(err);
      if (err instanceof ChangeWorkerUnavailableError) {
        notificateError(
          notifiacationInstance,
          '현재 해당 기능을 사용할 수 없습니다.',
          '서버에 담당 지도사 변경 기능이 아직 반영되지 않았습니다.',
        );
        return;
      }
      const message = (err as { response?: { data?: { message?: string } } })?.response
        ?.data?.message;
      notificateError(notifiacationInstance, '담당 지도사 변경에 실패했습니다.', message);
    }
  }, [fevent, plan, queryClient, notifiacationInstance, navigate, selected]);

  if (!isAdmin()) return <Navigate to="/" replace />;

  if (!fevent) {
    return (
      <div className="center-box h-screen-15">
        {feventState.status === 'error' ? (
          <p className="text-sm text-gray-500">상가 정보를 불러오지 못했습니다.</p>
        ) : (
          <Loading text="상가 정보를 불러오는 중" />
        )}
      </div>
    );
  }

  const currentUser = fevent.user;
  const targetBrandName = candidateBrandName(targetBrand);
  const confirmDisabled = !plan || plan.noop || plan.needsAgencyIndex || executing;

  return (
    <div className="space-y-4 p-4 pb-10">
      {/* 현재 귀속 */}
      <section className="space-y-1 rounded-lg border p-3">
        <p className="text-xs font-medium text-gray-500">현재 담당</p>
        <p className="font-bold">
          故 {fevent.deceasedInfo.name}
          {fevent.funeralHomeInfo?.name ? ` · ${fevent.funeralHomeInfo.name}` : ''}
        </p>
        <p className="text-sm">
          지도사 <span className="font-bold">{currentUser?.info?.name ?? '-'}</span>
          {currentUser?.username ? ` (${currentUser.username})` : ''}
          {currentUser?.info?.phoneNumber
            ? ` · ${phoneNumberStringFormator(currentUser.info.phoneNumber)}`
            : ''}
        </p>
        <p className="text-sm">
          상조 <span className="font-bold">{currentBrandName || '-'}</span>
          {typeof fevent.bugoBrandAgencyIndex === 'number' && currentBrand?.bugoAgencyList
            ? ` · 행사 상조 ${
                currentBrand.bugoAgencyList[fevent.bugoBrandAgencyIndex]?.corpName ?? ''
              }`
            : ''}
        </p>
        <p className="text-xs text-gray-500">
          상주 {fevent.memberOrderList?.length ?? 0}명 · 부고{' '}
          {(fevent.memberOrderList ?? []).filter((member) => member?.bugo).length}건이
          함께 재귀속됩니다.
        </p>
      </section>

      {/* 검색 */}
      <section className="space-y-2">
        <p className="text-xs font-medium text-gray-500">새 담당 지도사 검색</p>
        <div className="flex items-center gap-2">
          <input
            type="text"
            placeholder="이름 / 아이디 / 전화번호"
            className="w-full rounded-lg border px-3 py-2 text-sm font-bold theme-text-1 theme-bg-1 theme-border-1"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') search(keyword, brandFilterId);
            }}
          />
          <Button
            className="button-rectangle min-h-[36px] w-auto shrink-0 px-4 text-sm theme-text-4 theme-bg-main"
            onClick={() => search(keyword, brandFilterId)}
          >
            검색
          </Button>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={sameBrandOnly}
            onChange={(e) => setSameBrandOnly(e.target.checked)}
          />
          <span>
            같은 상조만
            {currentBrandName ? ` (${currentBrandName})` : ''}
          </span>
        </label>
        {!sameBrandOnly && (
          <p className="break-keep text-xs text-warn">
            다른 상조 계정도 검색됩니다. 다른 상조 지도사를 고르면 상가·부고의 상조 귀속이
            함께 바뀝니다.
          </p>
        )}
        <div className="max-h-[40vh] space-y-2 overflow-y-auto">
          {searching && (
            <div className="center-box h-24">
              <Loading />
            </div>
          )}
          {!searching && results && results.length === 0 && (
            <p className="py-6 text-center text-sm text-gray-400">
              검색 결과가 없습니다.
              {sameBrandOnly
                ? ' 다른 상조 계정은 "같은 상조만"을 해제하면 검색됩니다.'
                : ''}
            </p>
          )}
          {!searching &&
            results?.map((user) => {
              const isCurrent = user._id === currentUser?._id;
              const isSelected = user._id === selected?._id;
              const brandName = candidateBrandName(candidateBrand(user));
              return (
                <button
                  type="button"
                  key={user._id}
                  onClick={() => onSelect(user)}
                  className={`flex w-full items-center justify-between gap-2 rounded-lg border p-3 text-left ${
                    isSelected ? 'theme-border-main' : ''
                  }`}
                >
                  <div className="min-w-0 space-y-0.5">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="font-bold">
                        {user.info?.name ?? user.username}
                      </span>
                      {roleBadges(user.roles)}
                      {brandName && (
                        <span className="rounded border px-1.5 py-0.5 text-xs text-gray-600">
                          {brandName}
                        </span>
                      )}
                    </div>
                    <p className="truncate text-xs text-gray-500">
                      {user.username}
                      {user.info?.phoneNumber
                        ? ` · ${phoneNumberStringFormator(user.info.phoneNumber)}`
                        : ''}
                    </p>
                  </div>
                  <span className="shrink-0 text-xs font-medium text-gray-400">
                    {isCurrent ? '현재 담당' : isSelected ? '선택됨' : ''}
                  </span>
                </button>
              );
            })}
        </div>
      </section>

      {/* 변경 미리보기 */}
      {plan && selected && (
        <section className="space-y-2 rounded-lg border p-3">
          <p className="text-xs font-medium text-gray-500">변경 내용</p>
          <p className="text-sm">
            지도사 {currentUser?.info?.name ?? '-'} →{' '}
            <span className="font-bold">{selected.info?.name ?? selected.username}</span>
          </p>
          <p className="text-sm">
            상조 {currentBrandName || '-'} →{' '}
            <span className="font-bold">{targetBrandName || '-'}</span>
            <span
              className={`ml-2 rounded px-1.5 py-0.5 text-xs ${
                plan.brandChanged ? 'bg-warn text-white' : 'bg-gray-100 text-gray-600'
              }`}
            >
              {plan.brandChanged ? '상조 변경' : '동일'}
            </span>
          </p>
          {plan.brandChanged && targetAgencyList.length > 0 && (
            <label className="block text-sm">
              행사 상조
              <select
                className="mt-1 w-full rounded-lg border px-3 py-2 text-sm theme-bg-1"
                value={typeof chosenAgencyIndex === 'number' ? chosenAgencyIndex : ''}
                onChange={(e) =>
                  setChosenAgencyIndex(
                    e.target.value === '' ? undefined : Number(e.target.value),
                  )
                }
              >
                <option value="">선택</option>
                {targetAgencyList.map((agency, index) => (
                  <option key={index} value={index}>
                    {agency.corpName ?? agency.nickName ?? `#${index}`}
                  </option>
                ))}
              </select>
            </label>
          )}
          {plan.brandChanged && (
            <p className="break-keep text-xs text-warn">
              상조가 바뀌면 상가·부고의 상조 귀속과 부고장의 상조 로고·문구가 새 상조를
              따릅니다. 이미 발생한 화환·답례품 주문은 옛 상조의 주문 조회·정산에 그대로
              남습니다.
            </p>
          )}
          {plan.noop && (
            <p className="text-xs text-gray-500">이미 같은 지도사·상조입니다.</p>
          )}
          <Button
            className="button-rectangle min-h-[40px] theme-text-4 theme-bg-main"
            disabled={confirmDisabled}
            onClick={() => setConfirmOpen(true)}
          >
            {plan.needsAgencyIndex ? '행사 상조를 선택하세요' : '담당 지도사 변경'}
          </Button>
        </section>
      )}

      <DismissModal
        openId={CONFIRM_OPEN_ID}
        onClick={execute}
        buttonTitle="변경"
        title="담당 지도사 변경"
        subtitle={
          plan && selected
            ? `${currentUser?.info?.name ?? '-'} → ${
                selected.info?.name ?? selected.username
              }${
                plan.brandChanged
                  ? ` (상조 ${currentBrandName || '-'} → ${targetBrandName || '-'})`
                  : ''
              }. 상주 ${plan.memberCount}명 · 부고 ${
                plan.bugoCount
              }건이 함께 재귀속됩니다.`
            : ''
        }
      />
    </div>
  );
};
